/**
 * Payment-Link API-Naht — BFF-Proxy zur Payment-Link Provisioning-API v2.0 (Spec 493).
 *
 * Der `get`-Client prependet den `/v1`-Base, daher hitten diese Pfade
 * `GET /v1/admin/payment-link/...`. Platform-only am Route-/BFF-Gate; der BFF setzt den
 * dedizierten `X-Provisioning-Key` serverseitig (nie im FE). Inc 1: nur die zwei GETs.
 */
import { apiClient, get } from './client';
import type {
  PaymentLinkMerchantSetResponse,
  PaymentLinkMerchantStatus,
  PaymentLinkSaltRotationResponse,
  PaymentLinkServiceConfig,
  PaymentLinkServiceConfigRequest,
  PaymentLinkServiceConfigSetResponse,
  PaymentLinkServicesResponse,
} from '@/types/payment-link';

const SERVICES_PATH = '/admin/payment-link/services';

function merchantPath(merchantId: string): string {
  return `/admin/payment-link/merchants/${encodeURIComponent(merchantId)}`;
}

function serviceConfigPath(merchantId: string, serviceId: string): string {
  return `${merchantPath(merchantId)}/services/${encodeURIComponent(serviceId)}`;
}

/**
 * Alle Payment-Link-konfigurierten Services (Aggregat ueber alle Merchants), optional auf
 * einen Merchant gefiltert. Defaults haerten gegen eine duenne BFF-Antwort.
 */
export async function listPaymentLinkServices(
  merchantId?: string,
): Promise<PaymentLinkServicesResponse> {
  const params = merchantId ? { merchantId } : undefined;
  const raw = await get<PaymentLinkServicesResponse>(SERVICES_PATH, params);
  return { Services: raw.Services ?? [], Total: raw.Total ?? 0 };
}

/** Provisionierungs-Status eines Merchants. 404 (record_not_found) wirft als ApiError. */
export async function getPaymentLinkMerchant(
  merchantId: string,
): Promise<PaymentLinkMerchantStatus> {
  return get<PaymentLinkMerchantStatus>(merchantPath(merchantId));
}

/**
 * Merchant aktivieren/deaktivieren (Inc 2). Erstaktivierung liefert einmalig `SaltCleartext`;
 * Re-Aktivierung/Deaktivieren liefern die Antwort ohne Klartext. Der Aufrufer liest den
 * Klartext AUS DIESEM Response-Objekt (nie aus einem nachgelagerten GET).
 */
export async function setPaymentLinkMerchantEnabled(
  merchantId: string,
  enabled: boolean,
): Promise<PaymentLinkMerchantSetResponse> {
  // noRetry: eine Erstaktivierung, deren committete Antwort verloren geht, dürfte NICHT still
  // per Re-PUT wiederholt werden — der Re-PUT käme ohne SaltCleartext zurück (Reveal verloren).
  return apiClient<PaymentLinkMerchantSetResponse>(merchantPath(merchantId), {
    method: 'PUT',
    body: JSON.stringify({ Enabled: enabled }),
    noRetry: true,
  });
}

/** Salt rotieren (Inc 2). Liefert `SaltCleartext` genau einmal + den neuen Rotationszaehler. */
export async function rotatePaymentLinkSalt(
  merchantId: string,
): Promise<PaymentLinkSaltRotationResponse> {
  // noRetry: ein Blind-Retry könnte still doppelt rotieren (verteilte Links brechen).
  return apiClient<PaymentLinkSaltRotationResponse>(`${merchantPath(merchantId)}/rotate-salt`, {
    method: 'POST',
    noRetry: true,
  });
}

/**
 * Service-Config lesen (Inc 2b/3). Ein `404 service_not_configured` wirft als ApiError; der
 * Aufrufer liest das als „noch nicht konfiguriert"-Normalzustand (nicht als Fehler, D-NotConfigured).
 */
export async function getPaymentLinkServiceConfig(
  merchantId: string,
  serviceId: string,
): Promise<PaymentLinkServiceConfig> {
  return get<PaymentLinkServiceConfig>(serviceConfigPath(merchantId, serviceId));
}

/**
 * Service freischalten/konfigurieren (Inc 2b/3). Bei erstmaliger Click-Webhook-Aktivierung
 * liefert die Antwort einmalig `ClickWebhookSecretCleartext` — der Aufrufer liest den Klartext
 * AUS DIESEM Response-Objekt (nie aus einem nachgelagerten GET).
 */
export async function setPaymentLinkServiceConfig(
  merchantId: string,
  serviceId: string,
  body: PaymentLinkServiceConfigRequest,
): Promise<PaymentLinkServiceConfigSetResponse> {
  // noRetry: ein Blind-Retry bei Webhook-Erst-Aktivierung käme ohne ClickWebhookSecretCleartext
  // zurück (Reveal verloren) — wie beim Salt (Lehre #499-P2).
  return apiClient<PaymentLinkServiceConfigSetResponse>(serviceConfigPath(merchantId, serviceId), {
    method: 'PUT',
    body: JSON.stringify(body),
    noRetry: true,
  });
}

/** Service-Freischaltung entfernen (Inc 2b/3). Antwortet 204 (kein Body — apiClient 204-fest). */
export async function removePaymentLinkServiceConfig(
  merchantId: string,
  serviceId: string,
): Promise<void> {
  await apiClient<void>(serviceConfigPath(merchantId, serviceId), {
    method: 'DELETE',
    noRetry: true,
  });
}
